import { dom } from "@fortawesome/fontawesome-svg-core";
import { setAvatar, text } from "./dialog-card";

/**
 * Read-more (or the "read response" button) on a review card opens the full
 * text in a shared dialog instead of expanding inline. The single dialog
 * element is reused for every card and populated on open; closing/backdrop
 * handling is wired globally by initDialogs().
 *
 * Leaving a public response is a dedicated page: the dialog's respond link
 * mirrors the "leave a public response" / edit link rendered on the card.
 */

export const openReviewDialog = (card: HTMLElement) => {
  const dialog = document.getElementById("dialog-review") as HTMLDialogElement | null;
  if (!dialog) {
    return;
  }

  // The reviewer's profile URL, exposed by the card's author link (a card
  // renders none when the author has no profile to link to). Both the avatar
  // and the name link through to it.
  const authorEl = card.querySelector(".author");
  const authorName = text(authorEl);
  const authorLink = authorEl?.closest("a") as HTMLAnchorElement | null;
  const profileUrl = authorLink?.getAttribute("href") ? authorLink.href : null;

  const makeProfileLink = (className: string): HTMLAnchorElement => {
    const link = document.createElement("a");
    link.href = profileUrl as string;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.className = className;
    return link;
  };

  setAvatar(dialog.querySelector(".dialog-review-avatar"), card);

  const nameSlot = dialog.querySelector(".dialog-review-name");
  if (nameSlot) {
    if (profileUrl) {
      // stretched-link makes the whole (position-relative) header clickable,
      // so the avatar and rating link through to the profile as well.
      const link = makeProfileLink("text-reset text-decoration-none stretched-link");
      link.textContent = authorName;
      nameSlot.replaceChildren(link);
    } else {
      nameSlot.textContent = authorName;
    }
  }

  const ratingSlot = dialog.querySelector(".dialog-review-rating");
  const ratingSource = card.querySelector(".title .text-primary");
  if (ratingSlot) {
    ratingSlot.innerHTML = ratingSource ? ratingSource.innerHTML : "";
  }

  const contentSlot = dialog.querySelector(".dialog-review-content");
  const description = card.querySelector(".review-description");
  if (contentSlot) {
    contentSlot.textContent = description?.getAttribute("data-content") ?? text(description);
  }

  const dateSlot = dialog.querySelector(".dialog-review-date");
  if (dateSlot) {
    dateSlot.textContent = text(card.querySelector(".card-body > .text-gray-600 small"));
  }

  // Mirror the card's reply (owner-only) and "leave a public response" / edit link.
  // Reply data lives on the parent .slider-item as data attributes (set by
  // review_item.html.twig) so the card markup stays free of the reply content.
  const replyWrapper = dialog.querySelector(".dialog-review-reply");
  const sliderItem = card.closest(".slider-item") as HTMLElement | null;
  const replyContent = sliderItem?.dataset.replyContent;
  if (replyWrapper) {
    if (replyContent) {
      const replyName = replyWrapper.querySelector(".dialog-review-reply-name");
      const replyDate = replyWrapper.querySelector(".dialog-review-reply-date");
      const replyText = replyWrapper.querySelector(".dialog-review-reply-content");
      const replyAvatar = replyWrapper.querySelector(".dialog-review-reply-avatar");
      if (replyAvatar) {
        replyAvatar.innerHTML = "";
        const replyPhoto = sliderItem?.dataset.replyPhoto;
        if (replyPhoto) {
          const img = document.createElement("img");
          img.className = "thumb";
          img.src = replyPhoto;
          img.alt = "";
          img.loading = "lazy";
          img.style.width = "100%";
          img.style.height = "auto";
          replyAvatar.appendChild(img);
        }
      }
      if (replyName) {
        replyName.textContent = sliderItem?.dataset.replyName ?? "";
      }
      if (replyDate) {
        replyDate.textContent = sliderItem?.dataset.replyDate ?? "";
      }
      if (replyText) {
        replyText.textContent = replyContent;
      }

      const editLink = replyWrapper.querySelector(
        ".dialog-review-reply-edit",
      ) as HTMLAnchorElement | null;
      const editUrl = sliderItem?.dataset.replyEditUrl;
      if (editLink) {
        if (editUrl) {
          editLink.href = editUrl;
          editLink.classList.remove("d-none");
        } else {
          editLink.removeAttribute("href");
          editLink.classList.add("d-none");
        }
      }

      replyWrapper.classList.remove("d-none");
    } else {
      replyWrapper.classList.add("d-none");
    }
  }

  const respondLink = dialog.querySelector(
    ".dialog-review-respond-btn",
  ) as HTMLAnchorElement | null;
  const cardLink = card
    .closest(".slider-item")
    ?.querySelector(".reply-page-link") as HTMLAnchorElement | null;
  if (respondLink) {
    if (cardLink) {
      respondLink.href = cardLink.href;
      respondLink.classList.remove("d-none");
    } else {
      respondLink.removeAttribute("href");
      respondLink.classList.add("d-none");
    }
  }

  dom.i2svg();

  if (!dialog.open) {
    dialog.showModal();
  }
};
